import * as Phaser from "phaser";

// Single context-sensitive action button for touch devices.
// Tap → pick/drop  |  Hold → chop start  |  Release after hold → chop stop

const BUTTON_RADIUS = 48;     // button radius (diameter = 96px)
const CHOP_HOLD_MS = 200;     // ms hold before triggering chop (matches TouchInputHandler)
const TAP_THRESHOLD = 12;     // max pixel movement to still count as a tap

const BUTTON_COLOR_IDLE = 0x3366cc;
const BUTTON_COLOR_ACTIVE = 0x55aaff;
const BUTTON_COLOR_CHOPPING = 0xff6633;
const BUTTON_ALPHA_IDLE = 0.55;
const BUTTON_ALPHA_ACTIVE = 0.85;

export interface ActionButtonCallbacks {
    onPick: () => void;
    onChopStart: () => void;
    onChopStop: () => void;
}

export class VirtualActionButton {
    private _scene: Phaser.Scene;
    private _x: number;
    private _y: number;
    private _callbacks: ActionButtonCallbacks;

    private _circle: Phaser.GameObjects.Arc;
    private _label: Phaser.GameObjects.Text;

    private _activePointerId: number = -1;
    private _startX: number = 0;
    private _startY: number = 0;
    private _chopTimer: Phaser.Time.TimerEvent | null = null;
    private _isChopping: boolean = false;

    constructor(
        scene: Phaser.Scene,
        x: number,
        y: number,
        callbacks: ActionButtonCallbacks
    ) {
        this._scene = scene;
        this._x = x;
        this._y = y;
        this._callbacks = callbacks;
        this._createGraphics();
        this._registerPointerEvents();
    }

    private _createGraphics(): void {
        this._circle = this._scene.add.circle(
            this._x, this._y, BUTTON_RADIUS,
            BUTTON_COLOR_IDLE, BUTTON_ALPHA_IDLE
        ).setDepth(150).setScrollFactor(0);

        this._circle.setStrokeStyle(2, 0xffffff, 0.5);

        this._label = this._scene.add.text(this._x, this._y, 'ACT', {
            fontSize: '11px',
            color: '#ffffff',
            fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(151).setScrollFactor(0);
    }

    private _isInButtonZone(x: number, y: number): boolean {
        const dx = x - this._x;
        const dy = y - this._y;
        return Math.sqrt(dx * dx + dy * dy) <= BUTTON_RADIUS;
    }

    private _registerPointerEvents(): void {
        this._scene.input.on('pointerdown', this._onPointerDown, this);
        this._scene.input.on('pointermove', this._onPointerMove, this);
        this._scene.input.on('pointerup', this._onPointerUp, this);
        this._scene.input.on('pointercancel', this._onPointerCancel, this);
    }

    private _onPointerDown(pointer: Phaser.Input.Pointer): void {
        if (this._activePointerId !== -1) return;
        if (!this._isInButtonZone(pointer.x, pointer.y)) return;

        this._activePointerId = pointer.id;
        this._startX = pointer.x;
        this._startY = pointer.y;
        this._isChopping = false;

        this._circle.setFillStyle(BUTTON_COLOR_ACTIVE, BUTTON_ALPHA_ACTIVE);

        // Schedule chop after hold threshold
        this._chopTimer = this._scene.time.delayedCall(CHOP_HOLD_MS, () => {
            this._callbacks.onChopStart();
            this._isChopping = true;
            this._circle.setFillStyle(BUTTON_COLOR_CHOPPING, BUTTON_ALPHA_ACTIVE);
            this._label.setText('CHOP');
        });
    }

    private _onPointerMove(pointer: Phaser.Input.Pointer): void {
        if (pointer.id !== this._activePointerId) return;
        // Movement cancels chop timer (treat as drag, not hold)
        const dx = pointer.x - this._startX;
        const dy = pointer.y - this._startY;
        if (Math.sqrt(dx * dx + dy * dy) >= TAP_THRESHOLD) {
            this._cancelChopTimer();
        }
    }

    private _onPointerUp(pointer: Phaser.Input.Pointer): void {
        if (pointer.id !== this._activePointerId) return;
        this._release(pointer);
    }

    private _onPointerCancel(pointer: Phaser.Input.Pointer): void {
        if (pointer.id !== this._activePointerId) return;
        this._cancelChopTimer();
        if (this._isChopping) {
            this._callbacks.onChopStop();
            this._isChopping = false;
        }
        this._reset();
    }

    private _release(pointer: Phaser.Input.Pointer): void {
        if (this._isChopping) {
            this._cancelChopTimer();
            this._callbacks.onChopStop();
            this._isChopping = false;
            this._reset();
            return;
        }

        this._cancelChopTimer();

        const dx = pointer.x - this._startX;
        const dy = pointer.y - this._startY;
        const moved = Math.sqrt(dx * dx + dy * dy);
        if (moved < TAP_THRESHOLD) {
            this._callbacks.onPick();
        }

        this._reset();
    }

    private _cancelChopTimer(): void {
        if (this._chopTimer) {
            this._chopTimer.destroy();
            this._chopTimer = null;
        }
    }

    private _reset(): void {
        this._activePointerId = -1;
        this._circle.setFillStyle(BUTTON_COLOR_IDLE, BUTTON_ALPHA_IDLE);
        this._label.setText('ACT');
    }

    /** True when a pointer is actively touching this button. */
    isActive(): boolean {
        return this._activePointerId !== -1;
    }

    destroy(): void {
        this._scene.input.off('pointerdown', this._onPointerDown, this);
        this._scene.input.off('pointermove', this._onPointerMove, this);
        this._scene.input.off('pointerup', this._onPointerUp, this);
        this._scene.input.off('pointercancel', this._onPointerCancel, this);
        this._cancelChopTimer();
        this._circle.destroy();
        this._label.destroy();
    }
}
